'use client';

import Link from 'next/link';
import { LocaleLink } from '@/core/i18n';
import { ArrowLeft, ArrowRight, Clock, User, Tag, Calendar } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { BLOG_POST_CONTENT } from '@/core/i18n/content/blog-post.content';
import { BLOG_POST_ARTICLES } from '@/core/i18n/content/blog-post-articles.content';
import { BLOG_CONTENT } from '@/core/i18n/content/blog.content';
import type { BlogPostData, BlogSection } from './blog.data';
import type { BlogContent } from '@/core/i18n/content/blog.types';
import type { TranslatedBlogSection } from '@/core/i18n/content/blog-post-articles.types';
import { BLOG_POSTS, BLOG_CATEGORIES } from './blog.data';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Category colour map                                                       */
/* -------------------------------------------------------------------------- */

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  guides: { bg: 'bg-hms-teal/10', text: 'text-hms-teal' },
  industry: { bg: 'bg-hms-blue/10', text: 'text-hms-blue' },
  product: { bg: 'bg-hms-gold/10', text: 'text-hms-gold' },
  trends: { bg: 'bg-hms-green/10', text: 'text-hms-green' },
};

/* -------------------------------------------------------------------------- */
/*  ArticleHeader                                                             */
/* -------------------------------------------------------------------------- */

function ArticleHeader({ post, translatedPost, backLabel, categories, locale }: { post: BlogPostData; translatedPost: BlogPostData; backLabel: string; categories: BlogContent['categories']; locale: string }) {
  const { t } = useTranslation();
  const categoryColor = CATEGORY_COLORS[post.category] ?? CATEGORY_COLORS.guides;

  // Use locale-aware date formatting
  const localeMap: Record<string, string> = {
    en: 'en-IN', ar: 'ar-SA', th: 'th-TH', es: 'es-ES', fr: 'fr-FR',
    de: 'de-DE', ja: 'ja-JP', zh: 'zh-CN', hi: 'hi-IN',
  };
  const dateLocale = localeMap[locale] || 'en-IN';
  const formattedDate = new Date(post.publishedAt).toLocaleDateString(dateLocale, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  // Translate readTime
  const readTimeMatch = post.readTime.match(/^(\d+)\s*(min|minute)s?\s+(read)$/i);
  let readTimeDisplay = post.readTime;
  if (readTimeMatch) {
    const count = readTimeMatch[1];
    readTimeDisplay = `${count} ${t('blogPost.card.readTimeUnit')}`;
  }

  return (
    <header id="blog-post-header" className="relative w-full overflow-hidden bg-hms-bg">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 40%, rgba(200,145,58,0.07) 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 50% 50% at 50% 90%, rgba(13,158,153,0.03) 0%, transparent 70%)' }} />

      <div className="relative mx-auto max-w-3xl px-4 pb-10 pt-20 sm:px-6 sm:pt-28 lg:px-8">
        <LocaleLink id="blog-post-back-link" href="/blog" className={cn('mb-8 inline-flex items-center gap-2 text-sm font-medium text-hms-textSub', 'transition-colors duration-200 hover:text-hms-gold')}>
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </LocaleLink>

        <span id="blog-post-category" className={cn('mb-5 inline-flex items-center rounded-full px-3 py-1', 'text-[11px] font-semibold uppercase tracking-wider', categoryColor.bg, categoryColor.text)}>{categories[post.category]}</span>

        <h1 id="blog-post-title" className={cn('font-poppins text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-[2.75rem]', 'text-hms-text')}>{translatedPost.title}</h1>
        <p id="blog-post-description" className="mt-4 text-base leading-relaxed text-hms-textSub sm:text-lg">{translatedPost.description}</p>

        <div className="mt-8 flex items-center gap-4">
          <div id="blog-post-author-avatar" className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full', 'bg-hms-gold/10 text-sm font-bold text-hms-gold')}>{post.author.initials}</div>
          <div>
            <p id="blog-post-author-name" className="text-sm font-semibold text-hms-text">{translatedPost.author.name}</p>
            <p className="text-[12px] text-hms-textDim">{translatedPost.author.role}</p>
          </div>
          <span className="mx-2 h-4 w-px bg-hms-border" />
          <div className="flex items-center gap-4 text-[13px] text-hms-textSub">
            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-hms-textDim" />{formattedDate}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-hms-textDim" />{readTimeDisplay}</span>
          </div>
        </div>

        {post.tags.length > 0 && (
          <div id="blog-post-tags" className="mt-6 flex flex-wrap items-center gap-2">
            <Tag className="h-3.5 w-3.5 text-hms-textDim" />
            {post.tags.map((tag) => (
              <span key={tag} className={cn('rounded-md border border-hms-border/50 bg-hms-surface px-2.5 py-1', 'text-[11px] font-medium text-hms-textDim')}>{tag}</span>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/*  SectionRenderer                                                           */
/* -------------------------------------------------------------------------- */

function SectionRenderer({ section, index }: { section: TranslatedBlogSection | BlogSection; index: number }) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 id={`blog-post-section-${index}`} className={cn('mt-10 mb-4 text-xl font-bold tracking-tight sm:text-2xl', 'text-hms-text')}>
          <span className={cn('bg-gradient-to-r from-hms-gold/90 to-hms-goldLt', 'bg-clip-text text-transparent')}>{section.content}</span>
        </h2>
      );
    case 'paragraph':
      return <p id={`blog-post-section-${index}`} className="mb-5 text-[14px] leading-[1.85] text-hms-textSub">{section.content}</p>;
    case 'list':
      return (
        <div id={`blog-post-section-${index}`} className="mb-6">
          {section.content && <p className="mb-3 text-[14px] font-medium leading-relaxed text-hms-textSub">{section.content}</p>}
          <ul className="space-y-2.5 pl-1">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[13px] leading-relaxed text-hms-textSub">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-hms-gold/60" /><span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    case 'callout':
      return (
        <div id={`blog-post-section-${index}`} className={cn('my-8 rounded-xl border border-hms-gold/15 bg-hms-gold/[0.04]', 'pl-5 pr-6 py-5', 'border-l-[3px] border-l-hms-gold')}>
          <p className="text-[14px] leading-[1.85] text-hms-textSub">{section.content}</p>
        </div>
      );
    case 'cta': {
      const href = (section as BlogSection).href ?? '/resilience';
      return (
        <div id={`blog-post-section-${index}`} className="my-10 flex justify-center">
          <LocaleLink
            id={`blog-post-cta-inline-${index}`}
            href={href}
            className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]')}
          >
            {section.content}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      );
    }
    default: return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  RelatedPosts                                                              */
/* -------------------------------------------------------------------------- */

function RelatedPosts({ currentSlug, title, subtitle, categories, posts, locale }: { currentSlug: string; title: string; subtitle: string; categories: BlogContent['categories']; posts: BlogPostData[]; locale: string }) {
  const { t } = useTranslation();
  const related = posts.filter((p) => p.slug !== currentSlug).slice(0, 3);
  if (related.length === 0) return null;

  const localeMap: Record<string, string> = {
    en: 'en-IN', ar: 'ar-SA', th: 'th-TH', es: 'es-ES', fr: 'fr-FR',
    de: 'de-DE', ja: 'ja-JP', zh: 'zh-CN', hi: 'hi-IN',
  };
  const dateLocale = localeMap[locale] || 'en-IN';

  return (
    <section id="blog-related-posts" className="w-full bg-hms-surface py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 id="blog-related-title" className={cn('text-center text-2xl font-extrabold tracking-tight sm:text-3xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{title}</h2>
        <p id="blog-related-subtitle" className="mx-auto mt-3 max-w-md text-center text-sm text-hms-textSub">{subtitle}</p>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((post, idx) => {
            const catColor = CATEGORY_COLORS[post.category] ?? CATEGORY_COLORS.guides;
            const fDate = new Date(post.publishedAt).toLocaleDateString(dateLocale, { year: 'numeric', month: 'short', day: 'numeric' });
            const rtMatch = post.readTime.match(/^(\d+)\s*(min|minute)s?\s+(read)$/i);
            const rtDisplay = rtMatch ? `${rtMatch[1]} ${t('blogPost.card.readTimeUnit')}` : post.readTime;
            return (
              <article key={post.slug} id={`blog-related-card-${idx}`} className={cn('group flex flex-col rounded-xl border border-hms-border bg-hms-card', 'transition-all duration-300', 'hover:border-hms-gold/25 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20')}>
                <div className="flex flex-1 flex-col p-6">
                  <span className={cn('mb-3 inline-flex w-fit items-center rounded-full px-3 py-1', 'text-[11px] font-semibold uppercase tracking-wider', catColor.bg, catColor.text)}>{categories[post.category]}</span>
                  <LocaleLink id={`blog-related-link-${idx}`} href={`/blog/${post.slug}`} className="group/link">
                    <h3 className={cn('text-base font-bold leading-snug tracking-tight text-hms-text', 'transition-colors duration-200 group-hover/link:text-hms-gold')}>{post.title}</h3>
                  </LocaleLink>
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-hms-textSub line-clamp-2">{post.excerpt}</p>
                  <div className="mt-4 flex items-center gap-3 text-[11px] text-hms-textDim">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{rtDisplay}</span>
                    <span>{fDate}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogCTA                                                                   */
/* -------------------------------------------------------------------------- */

function BlogCTA({ ctaTitle, ctaSubtitle, ctaTrial, ctaBrowse }: { ctaTitle: string; ctaSubtitle: string; ctaTrial: string; ctaBrowse: string }) {
  return (
    <section id="blog-post-cta" className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }} />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2 id="blog-post-cta-title" className={cn('text-3xl font-extrabold tracking-tight sm:text-4xl', 'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400', 'bg-clip-text text-transparent')}>{ctaTitle}</h2>
        <p id="blog-post-cta-subtitle" className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">{ctaSubtitle}</p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink id="blog-post-cta-trial-btn" href="/register-hotel" className={cn('inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-bg', 'shadow-lg shadow-hms-gold/20 transition-all duration-200', 'hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]', 'w-full sm:w-auto justify-center')}>{ctaTrial}<ArrowRight className="h-4 w-4" /></LocaleLink>
          <LocaleLink id="blog-post-cta-blog-btn" href="/blog" className={cn('inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5', 'text-sm font-semibold tracking-wide text-hms-text', 'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold', 'w-full sm:w-auto')}>{ctaBrowse}</LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  BlogPost — Main Export                                                    */
/* -------------------------------------------------------------------------- */

export function BlogPost({ post }: { post: BlogPostData }) {
  const c = usePageContent(BLOG_POST_CONTENT);
  const blog = usePageContent(BLOG_CONTENT);
  const { locale } = useTranslation();

  // Get translated article content for current locale, fallback to English
  const localeArticles = BLOG_POST_ARTICLES[locale] || BLOG_POST_ARTICLES['en'] || {};
  const translatedPost = localeArticles[post.slug];
  
  // Build a merged BlogPostData with translated content, falling back to original
  const mergedPost: BlogPostData = translatedPost ? {
    ...post,
    title: translatedPost.title,
    description: translatedPost.description,
    excerpt: translatedPost.excerpt,
    author: {
      ...post.author,
      name: translatedPost.authorName,
      role: translatedPost.authorRole,
    },
    sections: translatedPost.sections as BlogSection[],
  } : post;

  // Build translated posts list for related articles
  const allTranslatedPosts = BLOG_POSTS.map((p) => {
    const tp = localeArticles[p.slug];
    return tp ? {
      ...p,
      title: tp.title,
      description: tp.description,
      excerpt: tp.excerpt,
      author: { ...p.author, name: tp.authorName, role: tp.authorRole },
      sections: tp.sections as BlogSection[],
    } : p;
  });

  return (
    <main id="blog-post-page">
      <ArticleHeader post={post} translatedPost={mergedPost} backLabel={c.backToBlog} categories={blog.categories} locale={locale} />
      <article id="blog-post-content" className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        {mergedPost.sections.map((section, idx) => (
          <SectionRenderer key={idx} section={section} index={idx} />
        ))}
      </article>
      <RelatedPosts currentSlug={post.slug} title={c.relatedArticles} subtitle={c.relatedSubtitle} categories={blog.categories} posts={allTranslatedPosts} locale={locale} />
      <BlogCTA ctaTitle={c.ctaTitle} ctaSubtitle={c.ctaSubtitle} ctaTrial={c.ctaTrial} ctaBrowse={c.ctaBrowse} />
    </main>
  );
}
